import { z } from 'zod';
import {
  SETTING_LIMITS,
  SPECIAL_ROLES,
  SPECIAL_ROLE_SETTING_KEYS,
  type SpecialRole,
} from './constants.js';

/**
 * Die Rolle eines Spielers — zugleich die einzige „Aufdeckungs-Währung" des
 * Spiels: sie fließt durch `PlayerView.role`, `SelfView.role`, das
 * Rundenergebnis und das Eliminierungs-Event. Sonderrollen sind deshalb hier
 * eingereiht statt in einem Parallelfeld: so gilt „bei Tod aufdecken, bei
 * Spielende alle" für sie automatisch mit.
 */
export const RoleSchema = z.enum(['CITIZEN', 'MAFIA', ...SPECIAL_ROLES] as const);
export type Role = z.infer<typeof RoleSchema>;

export const FactionSchema = z.enum(['CITIZENS', 'MAFIA']);
export type Faction = z.infer<typeof FactionSchema>;

export function isSpecialRole(role: Role): role is SpecialRole {
  return (SPECIAL_ROLES as readonly string[]).includes(role);
}

/**
 * Die Rolle, wie eine Aufdeckung im **laufenden** Spiel sie zeigt.
 *
 * Sonderrollen sind Bürgerrollen und werden beim Ausscheiden deshalb als
 * `CITIZEN` aufgedeckt. Sonst verriete jeder Tod nebenbei, welche Fähigkeit ab
 * jetzt aus dem Spiel ist — und wäre für die Mafia zugleich der Beweis, dass sie
 * einen Bürger erwischt hat. Namentlich aufgedeckt werden Sonderrollen erst bei
 * Spielende (GDD §22); dort läuft die Maske bewusst nicht mehr.
 */
export function maskSpecialRole(role: Role): Role {
  return isSpecialRole(role) ? 'CITIZEN' : role;
}

/**
 * Die Seite, für die eine Rolle gewinnt.
 *
 * Immer hierüber prüfen, nie über `role !== 'MAFIA'`: seit es Sonderrollen gibt,
 * heißt „nicht Mafia" nicht mehr „einfacher Bürger".
 */
export function factionOf(role: Role): Faction {
  return role === 'MAFIA' ? 'MAFIA' : 'CITIZENS';
}

export const GamePhaseSchema = z.enum([
  'LOBBY',
  'STARTING',
  'ROLE_REVEAL',
  'DISCUSSION',
  'VOTING',
  'TIE_BREAKER',
  'ROUND_RESULT',
  'GAME_OVER',
]);
export type GamePhase = z.infer<typeof GamePhaseSchema>;

/** GDD §31: Nur `WAITING`-Räume erscheinen in der öffentlichen Lobby. */
export const RoomStatusSchema = z.enum(['WAITING', 'STARTING', 'IN_GAME', 'FINISHED']);
export type RoomStatus = z.infer<typeof RoomStatusSchema>;

export function roomStatusForPhase(phase: GamePhase): RoomStatus {
  switch (phase) {
    case 'LOBBY':
      return 'WAITING';
    case 'STARTING':
      return 'STARTING';
    case 'GAME_OVER':
      return 'FINISHED';
    default:
      return 'IN_GAME';
  }
}

const seconds = (key: keyof typeof SETTING_LIMITS) =>
  z.number().int().min(SETTING_LIMITS[key].min).max(SETTING_LIMITS[key].max);

/**
 * Welche Sonderrollen der Host zugeschaltet hat.
 *
 * Bewusst ein Record und keine Liste: so ist „genau ein Eintrag pro Rolle" eine
 * Typ-Zusicherung statt einer Normalisierung, die der Server bei jedem Patch
 * nachholen müsste.
 */
export const SpecialRoleTogglesSchema = z.object({
  blessing: z.boolean(),
  voteBlock: z.boolean(),
  deadRedemption: z.boolean(),
  doubleUp: z.boolean(),
});
export type SpecialRoleToggles = z.infer<typeof SpecialRoleTogglesSchema>;

/** Die aktivierten Sonderrollen in der verbindlichen `SPECIAL_ROLES`-Reihenfolge. */
export function enabledSpecialRoles(toggles: SpecialRoleToggles): SpecialRole[] {
  return SPECIAL_ROLES.filter((role) => toggles[SPECIAL_ROLE_SETTING_KEYS[role]]);
}

export const GameSettingsSchema = z.object({
  mafiaCount: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  startCountdownSeconds: seconds('startCountdownSeconds'),
  discussionSeconds: seconds('discussionSeconds'),
  votingSeconds: seconds('votingSeconds'),
  tieBreakerSeconds: seconds('tieBreakerSeconds'),
  maxTieRounds: seconds('maxTieRounds'),
  /** Erlaubt den expliziten „Enthalten"-Button während der Abstimmung. */
  allowAbstain: z.boolean(),
  /**
   * Welche Rollen im Spiel sind, ist Regelwerk und damit öffentlich — wer sie
   * hält, ist das Geheimnis und steht ausschließlich in `PlayerState.role`.
   */
  specialRoles: SpecialRoleTogglesSchema,
});
export type GameSettings = z.infer<typeof GameSettingsSchema>;

export const DEFAULT_SETTINGS: GameSettings = {
  mafiaCount: 1,
  startCountdownSeconds: SETTING_LIMITS.startCountdownSeconds.default,
  discussionSeconds: SETTING_LIMITS.discussionSeconds.default,
  votingSeconds: SETTING_LIMITS.votingSeconds.default,
  tieBreakerSeconds: SETTING_LIMITS.tieBreakerSeconds.default,
  maxTieRounds: SETTING_LIMITS.maxTieRounds.default,
  allowAbstain: true,
  specialRoles: { blessing: false, voteBlock: false, deadRedemption: false, doubleUp: false },
};

/** `targetId === null` bedeutet Enthaltung. */
export const VoteSchema = z.object({
  voterId: z.string(),
  targetId: z.string().nullable(),
});
export type Vote = z.infer<typeof VoteSchema>;

export const TallyEntrySchema = z.object({
  playerId: z.string(),
  count: z.number().int().nonnegative(),
});
export type TallyEntry = z.infer<typeof TallyEntrySchema>;

export const RoundResultSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('ELIMINATION'),
    playerId: z.string(),
    role: RoleSchema,
    voteCount: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal('TIE'),
    playerIds: z.array(z.string()),
    voteCount: z.number().int().nonnegative(),
    /** Wie viele aufeinanderfolgende Unentschieden es nach dieser Abstimmung sind. */
    tieCount: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal('NO_ELIMINATION'),
    reason: z.literal('PROTECTED'),
    /**
     * Bewusst ohne `playerId`: „Nur Effekt, keine Namen" wird hier vom Schema
     * erzwungen und nicht der Disziplin des Clients überlassen.
     */
    voteCount: z.number().int().nonnegative(),
  }),
]);
export type RoundResult = z.infer<typeof RoundResultSchema>;

/**
 * Was eine Auszählung verändert hat — anonym, nur die Art.
 *
 * Nötig, weil die Historie sowohl die rohen Stimmen als auch die effektive
 * Auszählung zeigt: ohne diese Liste bliebe die Differenz unerklärt.
 */
export const BallotEffectSchema = z.enum([
  'PROTECTION',
  'VOTE_BLOCKED',
  'VOTE_DOUBLED',
  'DEAD_VOTE',
]);
export type BallotEffect = z.infer<typeof BallotEffectSchema>;

/**
 * Ein aufgelöster Wahlgang.
 *
 * Einträge entstehen ausschließlich im Moment der Auflösung — eine laufende
 * Abstimmung ist deshalb gar nicht erst Teil der Historie (GDD §24). Die
 * Geheimhaltung hängt damit nicht daran, dass der Client etwas ausblendet.
 */
export const RoundHistoryEntrySchema = z.object({
  roundNumber: z.number().int().positive(),
  /** 1 = reguläre Abstimmung, 2+ = Tie-Breaker innerhalb derselben Runde. */
  ballotNumber: z.number().int().positive(),
  isTieBreaker: z.boolean(),
  aliveAtStart: z.array(z.string()),
  candidateIds: z.array(z.string()),
  votes: z.array(VoteSchema),
  tally: z.array(TallyEntrySchema),
  result: RoundResultSchema,
  /** Anonyme Erklärung der Differenz zwischen `votes` und `tally`. */
  effects: z.array(BallotEffectSchema),
  resolvedAt: z.number().int(),
});
export type RoundHistoryEntry = z.infer<typeof RoundHistoryEntrySchema>;

export const GameOutcomeSchema = z.object({
  winner: FactionSchema,
  reason: z.enum(['ALL_MAFIA_ELIMINATED', 'MAFIA_PARITY', 'MAX_TIES']),
});
export type GameOutcome = z.infer<typeof GameOutcomeSchema>;

/** Warum der Host gerade nicht starten kann — der Client übersetzt das in Text. */
export const StartIssueSchema = z.enum([
  'NOT_ENOUGH_PLAYERS',
  'INVALID_MAFIA_COUNT',
  'TOO_MANY_SPECIAL_ROLES',
]);
export type StartIssue = z.infer<typeof StartIssueSchema>;
